// src/pages/e2e/MySubmissions.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { IDSButton, IDSAlert, IDSTabs, IDSTab, IDSTabPanel, IDSSelect } from "@inera/ids-react";
import { formatDateTimeNoSeconds } from "../../utils/dateTime";
import { SUBMISSION_STATUS_LABELS } from "../../utils/submissionStatus";
import {
  filterSubmissionsByStatus,
  partitionMySubmissions,
} from "../../utils/e2eSubmissionPolicy";
import { getMySubmissions } from "../../services/E2EAPI";
import SubmissionStatusBadge from "./SubmissionStatusBadge";

export default function MySubmissions() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSuccess, setShowSuccess] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    if (location.state?.successMessage) {
      setAlertMessage(location.state.successMessage);
      setShowSuccess(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    async function fetchMySubmissions() {
      try {
        const data = await getMySubmissions();
        setSubmissions(data);
      } catch (err) {
        console.error(err);
        setFetchError("Kunde inte hämta dina inlämningar. Försök ladda om sidan.");
      } finally {
        setLoading(false);
      }
    }

    if (isAuthenticated) {
      fetchMySubmissions();
    }
  }, [isAuthenticated]);

  const { activeSubmissions, finishedSubmissions } = partitionMySubmissions(submissions);

  const filteredActiveSubmissions = filterSubmissionsByStatus(activeSubmissions, statusFilter);
  const filteredFinishedSubmissions = filterSubmissionsByStatus(finishedSubmissions, statusFilter);

  const renderTable = (rows) => {
    if (rows.length === 0) {
      return <p className="ids-mt-4">Inga formulär att visa.</p>;
    }

    return (
      <table className="ids-mt-4" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "0.5rem 1rem" }}>Formulär</th>
            <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Status</th>
            <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Senast uppdaterad</th>
            <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Åtgärd</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.submissionId} style={{ height: '60px'}}>
              <td style={{ textAlign: "left", maxWidth: "300px", wordBreak: "break-word", padding: "0.5rem 1rem" }}>
                {s.form?.formTitle} v{s.form?.version}
              </td>
              <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                <SubmissionStatusBadge status={s.status} />
              </td>
              <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                {s.updatedAt
                  ? formatDateTimeNoSeconds(s.updatedAt)
                  : "—"}
              </td>
              <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                {s.status === "DRAFT" ? (
                  <IDSButton size="s" style={{ minWidth: "200px" }} onClick={() => navigate(`/e2e/submissions/${s.submissionId}/edit`)}>
                    Redigera
                  </IDSButton>
                ) : s.status === "IN_CLARIFY" ? (
                  <IDSButton size="s" style={{ minWidth: "200px" }} onClick={() => navigate(`/e2e/forms/${s.submissionId}/clarify`)}>
                    Klargör
                  </IDSButton>
                ) : (s.status === "SUBMITTED" || s.status === "IN_REVIEW") ? (
                    <IDSButton size="s" style={{ minWidth: "200px" }} onClick={() => navigate(`/e2e/submissions/${s.submissionId}`)}>
                      Visa
                    </IDSButton>
                ) 
                : (
                  <IDSButton size="s" style={{ minWidth: "200px" }} onClick={() => navigate(`/e2e/submissions/${s.submissionId}`)}>
                    Visa
                  </IDSButton>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  if (loading) {
    return <div>Laddar dina formulär…</div>;
  }

  if (fetchError) {
    return (
      <div className="ids-m-6">
        <main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto" }}>
          <IDSAlert type="error">
            <p>{fetchError}</p>
          </IDSAlert>
        </main>
      </div>
    );
  }

  return (
    <div className="ids-m-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto" }}>
        {showSuccess && (
          <div style={{ marginBottom: "2rem" }}>
            <IDSAlert ribbon type="success">
              {alertMessage}
            </IDSAlert>
          </div>
        )}
        <h2 className="ids-heading-l">Mina formulär</h2>
        <div className="ids-mb-4" style={{ maxWidth: "320px" }}>
          <IDSSelect
            label="Filtrera på status"
            id="mySubmissionStatusFilter"
            name="mySubmissionStatusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">Alla statusar</option>
            {Object.entries(SUBMISSION_STATUS_LABELS).map(([statusCode, statusLabel]) => (
              <option key={statusCode} value={statusCode}>
                {statusLabel}
              </option>
            ))}
          </IDSSelect>
        </div>
        <IDSTabs breakpoint="s" tabs={[
          <IDSTab label={`Aktiva formulär (${filteredActiveSubmissions.length})`} />,
          <IDSTab label={`Slutförda formulär (${filteredFinishedSubmissions.length})`} />
        ]}>
          <IDSTabPanel noFocus>
            {renderTable(filteredActiveSubmissions)}
          </IDSTabPanel>
          <IDSTabPanel noFocus>
            {renderTable(filteredFinishedSubmissions)}
          </IDSTabPanel>
        </IDSTabs>
      </main>
    </div>
  );
}
